/**
 * Meta Conversions API & Meta Pixel tracking helper
 * Ensures browser Pixel and server CAPI fire identical event_id, event_name, and respects marketing consent.
 */

export function generateEventId(prefix = 'lead_') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return prefix + crypto.randomUUID();
  }
  return prefix + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
}

export function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export function getFbc() {
  const fbcCookie = getCookie('_fbc');
  if (fbcCookie) return fbcCookie;
  if (typeof window !== 'undefined') {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const fbclid = urlParams.get('fbclid');
      if (fbclid) {
        return `fb.1.${Date.now()}.${fbclid}`;
      }
    } catch {}
  }
  return null;
}

// Clear any legacy test codes from browser storage to ensure production mode
if (typeof window !== 'undefined') {
  try {
    sessionStorage.removeItem('meta_test_event_code');
    localStorage.removeItem('meta_test_event_code');
    if ('META_TEST_EVENT_CODE' in window) {
      delete window.META_TEST_EVENT_CODE;
    }
  } catch {}
}

export function getTestEventCode() {
  // Production mode: test_event_code er fjernet efter fuldført test
  return null;
}

/**
 * Ensures Meta Pixel automatic button/form tracking (AutoConfig & Codeless) is disabled.
 */
export function disableMetaAutoConfig() {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    try {
      window.fbq('set', 'autoConfig', false, '1815315796480630');
      window.fbq('set', 'codeless', false, '1815315796480630');
    } catch {}
  }
}

// Automatically invoke on client load
if (typeof window !== 'undefined') {
  disableMetaAutoConfig();
}

export function hasMarketingConsent() {
  if (typeof window === 'undefined') return true;

  // 1. Check Cookiebot
  if (window.Cookiebot && window.Cookiebot.consent) {
    if (window.Cookiebot.consent.marketing === false) {
      return false;
    }
    if (window.Cookiebot.consent.marketing === true) {
      return true;
    }
  }

  // 2. Check CookieInformation
  if (window.CookieInformation && typeof window.CookieInformation.getConsentGivenFor === 'function') {
    try {
      const consent = window.CookieInformation.getConsentGivenFor('cookie_cat_marketing');
      if (consent === false) return false;
    } catch {}
  }

  // 3. Check Klaro
  if (window.klaro && typeof window.klaro.getManager === 'function') {
    try {
      const manager = window.klaro.getManager();
      if (manager && typeof manager.getConsent === 'function') {
        const consent = manager.getConsent('meta-pixel') ?? manager.getConsent('marketing');
        if (consent === false) return false;
      }
    } catch {}
  }

  // 4. Check custom / localStorage consent store
  try {
    const stored = localStorage.getItem('cookie_consent') || localStorage.getItem('user_consent');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.marketing === false || parsed.advertising === false) {
        return false;
      }
    }
  } catch {}

  return true;
}

/**
 * Fires deduplicated Lead event on both Meta Pixel (browser) and Meta Conversions API (server).
 * Only fires after successful form submission.
 */
export async function trackMetaLead({ eventId, email, phone, contentName }) {
  if (!hasMarketingConsent()) {
    console.info('[Meta Tracking] Lead event skipped: marketing consent denied by user.');
    return { skipped: true, reason: 'consent_denied' };
  }

  const id = eventId || generateEventId();
  const eventSourceUrl = typeof window !== 'undefined' ? window.location.href : 'https://mcautotransport.dk/';

  // Ensure Meta Pixel automatic tracking is disabled so duplicate ob3_plugin-set_ events are prevented
  disableMetaAutoConfig();

  // 1. Browser-side Meta Pixel
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    try {
      window.fbq(
        'track',
        'Lead',
        {
          content_name: contentName || 'Formular',
        },
        {
          eventID: id,
        }
      );
    } catch (e) {
      console.warn('[Meta Pixel] Error sending Lead:', e);
    }
  }

  // 2. Server-side Meta Conversions API (production mode: no test_event_code)
  try {
    const fbp = getCookie('_fbp');
    const fbc = getFbc();
    const testCode = getTestEventCode();

    const response = await fetch('/_fn/meta-capi-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        event_id: id,
        event_name: 'Lead',
        event_source_url: eventSourceUrl,
        email: email || null,
        phone: phone || null,
        fbp: fbp || null,
        fbc: fbc || null,
        content_name: contentName || null,
        ...(testCode ? { test_event_code: testCode } : {}),
      }),
    });

    const data = await response.json().catch(() => ({}));
    return { success: response.ok, data, eventId: id };
  } catch (err) {
    console.warn('[Meta CAPI] Error sending Lead to server endpoint:', err);
    return { success: false, error: err?.message, eventId: id };
  }
}

/**
 * Fires deduplicated Contact event on both Meta Pixel (browser) and Meta Conversions API (server).
 * Used when a visitor clicks a phone link, email link, or initiates direct contact.
 *
 * Krav:
 * 1. Samme serverintegration (meta-capi-lead), samme Pixel-ID og server-token.
 * 2. Send én Contact-hændelse via Pixel og én via CAPI med samme unikke ID (eventID / event_id) for deduplikering.
 * 3. Hændelsen registrerer klikket (fx "Klik på telefonlink" / "Klik på e-maillink") og må IKKE
 *    beskrives som et gennemført opkald eller en sendt e-mail.
 * 4. Respekterer markedsføringssamtykke (hasMarketingConsent).
 */
export async function trackMetaContact({
  eventId,
  channel, // 'phone' | 'email' | 'contact_link'
  target,  // phone number or email string
  contentName,
  testEventCode,
} = {}) {
  if (!hasMarketingConsent()) {
    console.info('[Meta Tracking] Contact event skipped: marketing consent denied by user.');
    return { skipped: true, reason: 'consent_denied' };
  }

  const id = eventId || generateEventId('contact_');
  const eventSourceUrl = typeof window !== 'undefined' ? window.location.href : 'https://mcautotransport.dk/';
  const activeTestCode = testEventCode || getTestEventCode();

  // Ensure Meta Pixel automatic tracking is disabled
  disableMetaAutoConfig();

  // Explicitly describe the action as a click, not a completed call or sent email
  const defaultContentName = channel === 'phone'
    ? 'Klik på telefonlink'
    : channel === 'email'
      ? 'Klik på e-maillink'
      : 'Klik på kontaktlink';

  const finalContentName = contentName || defaultContentName;

  // 1. Browser-side Meta Pixel
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    try {
      const pixelCustomData = {
        content_name: finalContentName,
        content_category: 'Kontakt',
      };
      if (channel) {
        pixelCustomData.contact_channel = channel;
      }
      if (activeTestCode) {
        pixelCustomData.test_event_code = activeTestCode;
      }

      window.fbq(
        'track',
        'Contact',
        pixelCustomData,
        {
          eventID: id,
        }
      );
    } catch (e) {
      console.warn('[Meta Pixel] Error sending Contact:', e);
    }
  }

  // 2. Server-side Meta Conversions API
  try {
    const fbp = getCookie('_fbp');
    const fbc = getFbc();

    const response = await fetch('/_fn/meta-capi-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        event_id: id,
        event_name: 'Contact',
        event_source_url: eventSourceUrl,
        fbp: fbp || null,
        fbc: fbc || null,
        content_name: finalContentName,
        ...(activeTestCode ? { test_event_code: activeTestCode } : {}),
      }),
    });

    const data = await response.json().catch(() => ({}));
    return { success: response.ok, data, eventId: id };
  } catch (err) {
    console.warn('[Meta CAPI] Error sending Contact to server endpoint:', err);
    return { success: false, error: err?.message, eventId: id };
  }
}

/**
 * Initializes automatic tracking on telephone and email link clicks across the entire site.
 * Throttles rapid duplicate clicks on the same link.
 */
export function initMetaContactTracking() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__metaContactTrackingInitialized) return;
  window.__metaContactTrackingInitialized = true;

  let lastClickTime = 0;
  let lastTarget = '';

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target?.closest?.('a[href^="tel:"], a[href^="mailto:"]');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      const now = Date.now();

      // Debounce duplicate clicks on identical link target within 1.5s
      if (href === lastTarget && now - lastClickTime < 1500) {
        return;
      }
      lastClickTime = now;
      lastTarget = href;

      if (href.startsWith('tel:')) {
        const rawPhone = href.replace(/^tel:/i, '').trim();
        trackMetaContact({
          channel: 'phone',
          target: rawPhone,
          contentName: 'Klik på telefonlink',
        });
      } else if (href.startsWith('mailto:')) {
        const rawEmail = href.replace(/^mailto:/i, '').split('?')[0].trim();
        trackMetaContact({
          channel: 'email',
          target: rawEmail,
          contentName: 'Klik på e-maillink',
        });
      }
    },
    { capture: true }
  );
}

// Automatically initialize on client load
if (typeof window !== 'undefined') {
  initMetaContactTracking();
}
